import React from 'react';
import { GeoPoint } from 'firebase/firestore';

export const emptyAreaResearch = {
  placeName: '',
  summary: '',
  hazards: [],
  entryExitAdvice: [],
  sources: []
};

const normalizeGeoPoint = (value) => {
  const latitude = value?.latitude ?? value?._latitude;
  const longitude = value?.longitude ?? value?._longitude;
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? new GeoPoint(latitude, longitude)
    : value;
};

export const normalizeAreaResearch = (research) => {
  const normalizedResearch = {
    ...research,
    placeName: (research.placeName || '').trim(),
    summary: (research.summary || '').trim(),
    hazards: (research.hazards || []).map((item) => item.trim()).filter(Boolean),
    entryExitAdvice: (research.entryExitAdvice || []).map((item) => item.trim()).filter(Boolean),
    sources: (research.sources || [])
      .map((source) => ({
        title: (source.title || '').trim(),
        url: (source.url || '').trim(),
        ...(source.originalUrl ? { originalUrl: source.originalUrl.trim() } : {})
      }))
      .filter((source) => source.title && source.url)
  };
  if (Object.prototype.hasOwnProperty.call(research, 'coordinate')) {
    normalizedResearch.coordinate = normalizeGeoPoint(research.coordinate);
  }
  if (Object.prototype.hasOwnProperty.call(research, 'startCoordinate')) {
    normalizedResearch.startCoordinate = normalizeGeoPoint(research.startCoordinate);
  }
  return normalizedResearch;
};

export const editableAreaResearch = (research) => {
  const normalizedResearch = normalizeAreaResearch(research);
  return {
    placeName: normalizedResearch.placeName,
    summary: normalizedResearch.summary,
    hazards: normalizedResearch.hazards,
    entryExitAdvice: normalizedResearch.entryExitAdvice,
    sources: normalizedResearch.sources
  };
};

const EditableTextList = ({ label, items, onChange }) => (
  <div className="known-swims-research-field">
    <strong>{label}</strong>
    {items.map((item, index) => (
      <div className="known-swims-research-list-item" key={`${label}-${index}`}>
        <textarea
          className="known-swims-research-list-text"
          rows="3"
          value={item}
          onChange={(event) => onChange(items.map((current, currentIndex) => currentIndex === index ? event.target.value : current))}
        />
        <button onClick={() => onChange(items.filter((_, currentIndex) => currentIndex !== index))}>Remove</button>
      </div>
    ))}
    <button className="secondary" onClick={() => onChange([...items, ''])}>Add item</button>
  </div>
);

const EditableSources = ({ sources, onChange }) => (
  <div className="known-swims-research-field">
    <strong>Sources</strong>
    {sources.map((source, index) => (
      <div className="known-swims-research-source-edit" key={`${source.url}-${index}`}>
        <input
          aria-label="Source title"
          placeholder="Source title"
          value={source.title}
          onChange={(event) => onChange(sources.map((current, currentIndex) => currentIndex === index ? { ...current, title: event.target.value } : current))}
        />
        <input
          aria-label="Source URL"
          placeholder="https://"
          value={source.url}
          onChange={(event) => onChange(sources.map((current, currentIndex) => currentIndex === index ? { ...current, url: event.target.value } : current))}
        />
        <button onClick={() => onChange(sources.filter((_, currentIndex) => currentIndex !== index))}>Remove</button>
      </div>
    ))}
    <button className="secondary" onClick={() => onChange([...sources, { title: '', url: '' }])}>Add source</button>
  </div>
);

const AreaResearchPanel = ({ research, loading, editing, researching, saving, canResearch, onResearch, onEdit, onCancel, onSave, onChange, readOnly = false }) => {
  if (!canResearch) return null;
  if (loading) return <p>Loading area research...</p>;

  if (!research && !editing) {
    if (readOnly) return <p>No area research is available for this swim.</p>;
    return <button className="known-swims-research-button" onClick={onResearch} disabled={researching}>{researching ? 'Researching...' : 'Research area'}</button>;
  }

  if (!editing) {
    return (
      <section className="known-swims-research-panel">
        <h3>Area research</h3>
        <strong>{research.placeName}</strong>
        <p>{research.summary}</p>
        {research.hazards?.length > 0 && <p><strong>Hazards:</strong> {research.hazards.join(' ')}</p>}
        {research.entryExitAdvice?.length > 0 && <p><strong>Entry / exit:</strong> {research.entryExitAdvice.join(' ')}</p>}
        {research.sources?.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer">{source.title}</a>)}
        {!readOnly && (
          <div className="known-swims-button-row">
            <button onClick={onEdit}>Edit research</button>
            <button className="secondary" onClick={onResearch} disabled={researching}>{researching ? 'Researching...' : 'Refresh research'}</button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="known-swims-research-panel">
      <h3>Edit area research</h3>
      <label htmlFor="area-research-place-name">Place name</label>
      <input id="area-research-place-name" value={research.placeName || ''} onChange={(event) => onChange('placeName', event.target.value)} />
      <label htmlFor="area-research-summary">Summary</label>
      <textarea id="area-research-summary" rows="5" value={research.summary || ''} onChange={(event) => onChange('summary', event.target.value)} />
      <EditableTextList label="Hazards" items={research.hazards || []} onChange={(value) => onChange('hazards', value)} />
      <EditableTextList label="Entry / exit advice" items={research.entryExitAdvice || []} onChange={(value) => onChange('entryExitAdvice', value)} />
      <EditableSources sources={research.sources || []} onChange={(value) => onChange('sources', value)} />
      <div className="known-swims-button-row">
        <button onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save research'}</button>
        <button className="secondary" onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
};

export default AreaResearchPanel;
